import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, GitBranch, Search, RefreshCw, ChevronDown, ChevronRight, Clock, Code, Brain, Zap, Filter, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useProvenance } from '../../hooks/useProvenance';
import { useChatStore } from '../../stores/useChatStore';
import { ProvenanceEvent } from '../../types/legacyControls';

interface ProvenancePageProps {
  isOpen: boolean;
  onClose: () => void;
  fullScreen?: boolean;
}



const ProvenancePage: React.FC<ProvenancePageProps> = ({ isOpen, onClose, fullScreen = true }) => {
  const [events, setEvents] = useState<ProvenanceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('All');
  const [selectedAgent, setSelectedAgent] = useState('All');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [selectedEvent, setSelectedEvent] = useState<ProvenanceEvent | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const { sessionId } = useChatStore();
  useProvenance(sessionId || '');

  const loadEvents = useCallback(async () => {
    if (!sessionId) return;
    
    setLoading(true);
    try {
      const mockEvents: ProvenanceEvent[] = [
        {
          id: 'event_001',
          ts: new Date(Date.now() - 120000),
          kind: 'prompt_sent',
          summary: 'User requested implementation of Legacy Controls as full-page components',
          detailsId: 'detail_001',
          details: {
            prompt: 'Please rebuild the three Legacy Controls components (Patch History, Agent Memory, Provenance) as full-blown pages in the Beehive workspace, following the Knowledge Base pattern with enhanced features like filtering, search, export capabilities, and detailed views.',
            response: 'I understand. I will create three new page components following the Knowledge Base pattern with enhanced features including filtering, search, export capabilities, and detailed views. Let me start by creating the Patch History page component.'
          },
          agent: 'Devin AI',
          status: 'success',
          duration: 2.5
        },
        {
          id: 'event_002',
          ts: new Date(Date.now() - 180000),
          kind: 'patch_applied',
          summary: 'Created Patch History page component with diff viewer and export functionality',
          detailsId: 'detail_002',
          details: {
            patchInfo: {
              filesChanged: ['src/pages/beehive/patch-history.tsx'],
              linesAdded: 245,
              linesRemoved: 0,
              description: 'Implement comprehensive Patch History page with filtering, search, diff viewer modal, and export capabilities'
            }
          },
          agent: 'Devin AI',
          status: 'success',
          duration: 8.2
        },
        {
          id: 'event_003',
          ts: new Date(Date.now() - 240000),
          kind: 'memory_snapshot',
          summary: 'Stored knowledge about Knowledge Base pattern implementation',
          detailsId: 'detail_003',
          details: {
            memorySnapshot: {
              context: 'Knowledge Base pattern analysis',
              insights: [
                'Full-screen layout with header, filters, and content areas',
                'Glass-dark styling with backdrop-blur-xl',
                'Consistent search and filter UI patterns',
                'Animation and transition effects using framer-motion'
              ],
              confidence: 95
            }
          },
          agent: 'Devin AI',
          status: 'success',
          duration: 1.8
        },
        {
          id: 'event_004',
          ts: new Date(Date.now() - 300000),
          kind: 'plugin_run',
          summary: 'Analyzed existing Legacy Controls components for feature mapping',
          detailsId: 'detail_004',
          details: {
            pluginData: {
              plugin: 'CodeAnalyzer',
              input: {
                files: ['src/components/HistoryPanel.tsx', 'src/components/Beehive/AgentMemoryPanel.tsx', 'src/components/Beehive/ProvenancePanel.tsx'],
                analysis_type: 'feature_extraction'
              },
              output: {
                features_found: ['patch listing', 'revert functionality', 'memory display', 'provenance timeline'],
                gaps_identified: ['export functionality', 'advanced filtering', 'detailed views', 'full-screen layout'],
                recommendations: ['Follow Knowledge Base pattern', 'Implement glass-dark styling', 'Add comprehensive search']
              }
            }
          },
          agent: 'CodeAnalyzer Plugin',
          status: 'success',
          duration: 4.7
        },
        {
          id: 'event_005',
          ts: new Date(Date.now() - 420000),
          kind: 'task_created',
          summary: 'Created task to rebuild Legacy Controls as full-page components',
          detailsId: 'detail_005',
          details: {
            taskInfo: {
              title: 'Rebuild Legacy Controls as Full-Page Components',
              description: 'Transform Patch History, Agent Memory, and Provenance from flyout panels to full-page components',
              priority: 'high',
              estimatedEffort: '4-6 hours',
              dependencies: ['Knowledge Base pattern', 'API endpoints', 'UI components']
            }
          },
          agent: 'Task Planner',
          status: 'success',
          duration: 0.5
        },
        {
          id: 'event_006',
          ts: new Date(Date.now() - 480000),
          kind: 'decision_made',
          summary: 'Decided to follow Knowledge Base pattern for consistent UX',
          detailsId: 'detail_006',
          details: {
            prompt: 'Should we create new components from scratch or modify existing ones?',
            response: 'Based on the user requirements and existing Knowledge Base implementation, we should create new page components that follow the established pattern. This ensures consistency and allows us to implement all the enhanced features like export, advanced filtering, and detailed views.',
            reasoning: [
              'Knowledge Base pattern is well-established and user-approved',
              'Full-page layout provides better UX for complex data',
              'Glass-dark styling maintains design consistency',
              'Enhanced features require significant architectural changes'
            ]
          },
          agent: 'Devin AI',
          status: 'success',
          duration: 3.2
        }
      ];
      
      setEvents(mockEvents);
    } catch (error) {
      console.error('Failed to load provenance events:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen) {
      loadEvents();
    }
  }, [sessionId, isOpen, loadEvents]);

  const toggleEventExpansion = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const handleViewDetails = (event: ProvenanceEvent) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const getFilteredEvents = () => {
    return events.filter(event => {
      const matchesSearch = event.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           event.kind.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (event.agent && event.agent.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesEventType = selectedEventType === 'All' || event.kind === selectedEventType;
      const matchesAgent = selectedAgent === 'All' || event.agent === selectedAgent;
      
      return matchesSearch && matchesEventType && matchesAgent;
    });
  };

  const filteredEvents = getFilteredEvents();

  const getEventIcon = (kind: string) => {
    switch (kind) {
      case 'prompt_sent':
        return <Zap className="w-4 h-4 text-blue-400" />;
      case 'plugin_run':
        return <Code className="w-4 h-4 text-green-400" />;
      case 'patch_applied':
        return <GitBranch className="w-4 h-4 text-purple-400" />;
      case 'memory_snapshot':
        return <Brain className="w-4 h-4 text-yellow-400" />;
      case 'task_created':
        return <Clock className="w-4 h-4 text-orange-400" />;
      case 'decision_made':
        return <Eye className="w-4 h-4 text-pink-400" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getEventTypeLabel = (kind: string) => {
    switch (kind) {
      case 'prompt_sent':
        return 'Prompt Sent';
      case 'plugin_run':
        return 'Plugin Run';
      case 'patch_applied':
        return 'Patch Applied';
      case 'memory_snapshot':
        return 'Memory Snapshot';
      case 'task_created':
        return 'Task Created';
      case 'decision_made':
        return 'Decision Made';
      default:
        return kind.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'text-green-400 bg-green-400/10';
      case 'failed':
        return 'text-red-400 bg-red-400/10';
      case 'pending':
        return 'text-yellow-400 bg-yellow-400/10';
      default:
        return 'text-slate-400 bg-slate-400/10';
    }
  };

  if (!fullScreen) return null;

  return (
    <>
      <div className="flex flex-col h-full w-full bg-slate-900">
        <div className="flex items-center justify-between p-6 border-b border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Provenance</h2>
            <p className="text-slate-400 mt-1">
              Offer a transparent "chain-of-thought" audit trail: how each AI-generated change or suggestion was arrived at.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input
                placeholder="Search events by summary, type, or agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 violett-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-slate-400" />
              <select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Events</option>
                <option value="prompt_sent">Prompt Sent</option>
                <option value="plugin_run">Plugin Run</option>
                <option value="patch_applied">Patch Applied</option>
                <option value="memory_snapshot">Memory Snapshot</option>
                <option value="task_created">Task Created</option>
                <option value="decision_made">Decision Made</option>
              </select>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="violett-input"
              >
                <option value="All">All Agents</option>
                <option value="Devin AI">Devin AI</option>
                <option value="CodeAnalyzer Plugin">CodeAnalyzer Plugin</option>
                <option value="Task Planner">Task Planner</option>
              </select>
              <Button variant="ghost" size="icon" onClick={loadEvents} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto min-h-0">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-accent via-slate-600 to-transparent"></div>
            
            <div className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-400">Loading provenance events...</span>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-slate-400">
                  <GitBranch className="w-12 h-12 mb-4 opacity-50" />
                  <h3 className="text-lg font-medium text-slate-300 mb-2">No events found</h3>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              ) : (
                filteredEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative pl-20"
                  >
                    {/* Timeline node */}
                    <div className="absolute left-6 top-6 w-4 h-4 rounded-full bg-slate-800 border-2 border-brand-accent flex items-center justify-center">
                      {getEventIcon(event.kind)}
                    </div>
                    
                    <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300">
                      <CardHeader 
                        className="cursor-pointer"
                        onClick={() => toggleEventExpansion(event.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="text-sm font-medium text-brand-accent">
                                {getEventTypeLabel(event.kind)}
                              </span>
                              {event.status && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                                  {event.status}
                                </span>
                              )}
                              {event.duration && (
                                <span className="text-xs text-slate-400">
                                  {event.duration}s
                                </span>
                              )}
                            </div>
                            <CardTitle className="text-lg cream-gradient-text text-drop-shadow mb-2">{event.summary}</CardTitle>
                            <div className="flex items-center space-x-4 text-sm text-slate-400">
                              <div className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>{event.ts.toLocaleString()}</span>
                              </div>
                              {event.agent && (
                                <span className="bg-slate-700 px-2 py-1 rounded text-xs">{event.agent}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(event);
                              }}
                              className="h-8 px-3 text-xs"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Details
                            </Button>
                            {expandedEvents.has(event.id) ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      
                      <AnimatePresence>
                        {expandedEvents.has(event.id) && event.details && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <CardContent className="pt-0">
                              <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4 space-y-3">
                                {event.details.prompt && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Prompt:</h5>
                                    <p className="text-slate-400 text-sm">{event.details.prompt}</p>
                                  </div>
                                )}
                                {event.details.response && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Response:</h5>
                                    <p className="text-slate-400 text-sm">{event.details.response}</p>
                                  </div>
                                )}
                                {event.details.pluginData && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Plugin Data:</h5>
                                    <pre className="text-slate-400 text-xs bg-slate-900 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(event.details.pluginData, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {event.details.memorySnapshot && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Memory Snapshot:</h5>
                                    <pre className="text-slate-400 text-xs bg-slate-900 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(event.details.memorySnapshot, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {event.details.patchInfo && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Patch Information:</h5>
                                    <pre className="text-slate-400 text-xs bg-slate-900 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(event.details.patchInfo, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                {event.details.taskInfo && (
                                  <div>
                                    <h5 className="text-sm font-medium text-slate-300 mb-2">Task Information:</h5>
                                    <pre className="text-slate-400 text-xs bg-slate-900 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(event.details.taskInfo, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {showDetailModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="text-lg font-semibold text-white">Event Details</h3>
                <p className="text-sm text-slate-400">{getEventTypeLabel(selectedEvent.kind)} - {selectedEvent.agent}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">{selectedEvent.summary}</h4>
                  <div className="flex items-center space-x-4 text-sm text-slate-400 mb-4">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{selectedEvent.ts.toLocaleString()}</span>
                    </div>
                    {selectedEvent.status && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedEvent.status)}`}>
                        {selectedEvent.status}
                      </span>
                    )}
                    {selectedEvent.duration && (
                      <span>Duration: {selectedEvent.duration}s</span>
                    )}
                  </div>
                </div>
                
                {selectedEvent.details && (
                  <div className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                    <pre className="text-slate-300 text-sm whitespace-pre-wrap overflow-x-auto">
                      {JSON.stringify(selectedEvent.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ProvenancePage;
